import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { intermediairesAPI } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import DeleteCascadeModal from '../shared/DeleteCascadeModal';
import { theme, pageTitleStyle, pageTitleTextStyle, primaryButtonStyle } from '../../theme';

const ORANGE = theme.orange;
const STATUT_BG  = { actif:'#E1F5EE', suspendu:'#FCEBEB' };
const STATUT_CLR = { actif:'#0F6E56', suspendu:'#A32D2D' };
const TYPE_BG    = { courtier:'#E6F1FB', avocat:'#FAEEDA', association:'#E1F5EE', organisme_formation:'#EEEDFE' };
const TYPE_CLR   = { courtier:'#185FA5', avocat:'#854F0B', association:'#0F6E56', organisme_formation:'#534AB7' };

const SvgIcon = ({ size = 16, color = 'currentColor', children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    {children}
  </svg>
);
const initials = nom => (nom || '?').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();

export default function ListeIntermediaires() {
  const navigate = useNavigate();
  const { t }    = useLanguage();
  const { isIT, isReadOnly } = useAuth();
  const [items, setItems]     = useState([]);
  const [search, setSearch]   = useState('');
  const [pays, setPays]       = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [confirm, setConfirm] = useState(null);
  const [searchFocus, setSearchFocus] = useState(false);

  const PAYS_LIST = ['France','Belgique','Allemagne','Espagne','Italie','Pays-Bas'];
  const TYPE_LABELS = {
    courtier: t('intermediaires.type_courtier'),
    avocat: t('intermediaires.type_avocat'),
    association: t('intermediaires.type_association'),
    organisme_formation: t('intermediaires.type_formation'),
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await intermediairesAPI.list({ search, pays, statut: 'tous' });
      setItems(data);
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }, [search, pays]);

  useEffect(() => { load(); }, [load]);

  async function handleSuspend(id, statut) {
    try { await intermediairesAPI.setStatut(id, statut === 'actif' ? 'suspendu' : 'actif'); load(); }
    catch(e) { alert(e.message); }
  }

  return (
    <div>
      {/* En-tête : titre souligné (style "Ligne") */}
      <div style={{ ...pageTitleStyle, marginBottom:16 }}>
        <span style={pageTitleTextStyle}>
          <SvgIcon size={22} color={ORANGE}><path d="M3 7m0 2a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v9a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2zM8 7v-2a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v2M12 12l0 .01M3 13a20 20 0 0 0 18 0"/></SvgIcon>
          {t('intermediaires.title')}
        </span>
        <span style={{ fontSize:12, color:theme.textTertiary }}>
          {items.length} {items.length !== 1 ? t('intermediaires.count_plural') : t('intermediaires.count')}
        </span>
        {!isReadOnly && (
        <button onClick={() => navigate('/intermediaires/nouveau')}
          style={{ ...primaryButtonStyle, marginLeft:'auto' }}>
          {t('intermediaires.create_btn')}
        </button>
        )}
      </div>

      {/* Recherche + pays */}
      <div style={{ display:'flex', gap:10, marginBottom:16, alignItems:'center', flexWrap:'wrap' }}>
        <div style={{ flex:1, minWidth:220, display:'flex', alignItems:'center', gap:8,
          border:`1px solid ${searchFocus ? ORANGE : '#D9D9D5'}`, borderRadius:8, padding:'0 12px', background:'#fff',
          boxShadow: searchFocus ? '0 0 0 3px rgba(241,138,0,0.15)' : 'none', transition:'all .12s' }}>
          <SvgIcon size={16} color="#A0A0A0"><path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0M21 21l-6 -6"/></SvgIcon>
          <input value={search} onChange={e => setSearch(e.target.value)}
            onFocus={()=>setSearchFocus(true)} onBlur={()=>setSearchFocus(false)}
            placeholder={t('intermediaires.search_placeholder')}
            style={{ flex:1, border:'none', outline:'none', padding:'10px 0', fontSize:14, fontFamily:'inherit', background:'transparent' }} />
        </div>
        <select value={pays} onChange={e => setPays(e.target.value)}
          style={{ border:'1px solid #D9D9D5', borderRadius:8, padding:'10px 12px', fontSize:14, minWidth:150, fontFamily:'inherit', cursor:'pointer' }}>
          <option value="">{t('common.all_countries')}</option>
          {PAYS_LIST.map(p => <option key={p}>{p}</option>)}
        </select>
      </div>

      {error && <div style={{ color:'#A32D2D', marginBottom:12, fontSize:13 }}>{t('common.error')} : {error}</div>}
      {loading ? <div style={{ color:'#64748B', padding:24 }}>{t('common.loading')}</div> : (
        <div style={{ background:'#fff', borderRadius:12, border:'0.5px solid #E8E8E5', overflow:'hidden' }}>
          {items.map((item, idx) => (
            <div key={item.id}>
              <div onClick={() => navigate(`/intermediaires/${item.id}`)}
                style={{ display:'flex', alignItems:'center', gap:13, padding:'14px 18px', cursor:'pointer',
                  borderBottom: idx < items.length - 1 ? '0.5px solid #F1F1EE' : 'none' }}
                onMouseEnter={e => e.currentTarget.style.background='#FAFAF8'}
                onMouseLeave={e => e.currentTarget.style.background=''}>
                <div style={{ width:38, height:38, borderRadius:9, flexShrink:0, background:'#F1F1EE',
                  display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:500, color:'#64748B' }}>
                  {initials(item.nom)}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:14, fontWeight:500, color:'#1A1A1A' }}>{item.nom}</div>
                  <div style={{ fontSize:12, color:'#64748B', marginTop:1 }}>
                    {item.nb_contacts} {item.nb_contacts !== 1 ? t('intermediaires.contacts_count_plural') : t('intermediaires.contacts_count')}
                    {item.statut === 'suspendu' && <span style={{ color:'#A32D2D', marginLeft:6 }}>· {t('intermediaires.not_assignable')}</span>}
                  </div>
                </div>
                <span style={{ background: TYPE_BG[item.type]||'#F1F5F9', color: TYPE_CLR[item.type]||'#475569',
                  padding:'4px 10px', borderRadius:99, fontSize:11, fontWeight:500 }}>
                  {TYPE_LABELS[item.type] || item.type}
                </span>
                <span style={{ background: STATUT_BG[item.statut]||'#F1F5F9', color: STATUT_CLR[item.statut]||'#475569',
                  padding:'4px 10px', borderRadius:99, fontSize:11, fontWeight:500 }}>
                  {item.statut === 'actif' ? t('status.actif') : t('status.suspendu')}
                </span>
                <div style={{ display:'flex', gap:4 }} onClick={e=>e.stopPropagation()}>
                  {!isReadOnly && (
                  <button onClick={() => navigate(`/intermediaires/${item.id}`)} title={t('common.modify')}
                    style={{ width:30, height:30, borderRadius:7, cursor:'pointer', background:'#F7F7F5', border:'none', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <SvgIcon size={15} color="#64748B"><path d="M9 7h-3a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-3M9 15h3l8.5 -8.5a1.5 1.5 0 0 0 -3 -3l-8.5 8.5v3M16 5l3 3"/></SvgIcon>
                  </button>
                  )}
                  {!isReadOnly && (
                  <button onClick={() => handleSuspend(item.id, item.statut)} title={item.statut === 'actif' ? t('common.suspend') : t('common.reactivate')}
                    style={{ width:30, height:30, borderRadius:7, cursor:'pointer', border:'none', display:'flex', alignItems:'center', justifyContent:'center',
                      background: item.statut === 'actif' ? '#FAEEDA' : '#E1F5EE' }}>
                    {item.statut === 'actif'
                      ? <SvgIcon size={15} color="#854F0B"><path d="M6 5v14M18 5v14"/></SvgIcon>
                      : <SvgIcon size={15} color="#0F6E56"><path d="M7 4v16l13 -8z"/></SvgIcon>}
                  </button>
                  )}
                  {isIT && (
                    <button onClick={() => setConfirm({ id: item.id, nom: item.nom })} title={t('it_delete.title')}
                      style={{ width:30, height:30, borderRadius:7, cursor:'pointer', background:'#FCEBEB', border:'none', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <SvgIcon size={15} color="#A32D2D"><path d="M4 7h16M10 11v6M14 11v6M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"/></SvgIcon>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
          {items.length === 0 && <div style={{ padding:32, textAlign:'center', color:'#94A3B8', fontSize:14 }}>{t('common.no_results')}</div>}
        </div>
      )}

      {/* Modal de suppression God Mode (IT uniquement) */}
      <DeleteCascadeModal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirmed={() => load()}
        entityType="intermediaire"
        entityId={confirm?.id}
        entityLabel={confirm?.nom}
      />
    </div>
  );
}
